// ============================================================
// AppErrorBoundary.tsx
// Root error boundary. Without one, ANY render-time throw (a malformed
// record, an editor/Yjs failure) unmounted the whole tree → a blank white
// screen with no way back except a manual refresh. This catches it, shows a
// small recoverable fallback, and — before reloading — tries to flush any
// pending case autosave so the user's last edit isn't lost.
// ============================================================
import React from 'react'
import { flushAutoSave } from '../../hooks/useAutoSave'

interface State { error: Error | null }

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info.componentStack)
  }

  private handleReload = (): void => {
    const reload = (): void => { window.location.reload() }
    // Best-effort save, capped so a hung request can't block recovery.
    Promise.race([flushAutoSave(), new Promise((r) => setTimeout(r, 3000))])
      .catch(() => undefined)
      .finally(reload)
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div className="min-h-screen bg-casevo-offwhite font-sans flex items-center justify-center p-4">
        <div role="alert" className="w-full max-w-md bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8 text-center">
          <h1 className="text-lg font-medium text-casevo-forest">Something went wrong</h1>
          <p className="text-sm text-casevo-text-soft mt-2">
            This screen hit an unexpected error. Reloading will try to save your latest change first.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-5 inline-flex items-center justify-center font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors text-sm rounded-lg px-5 py-2.5 focus:outline-none casevo-focus"
          >
            Reload Casevo
          </button>
        </div>
      </div>
    )
  }
}

export default AppErrorBoundary
